// packages/agent-kit/src/hooks/admin/useUserActions.ts
import { useState, useCallback } from 'react';
import { useSWRConfig } from 'swr';
import { toast } from '@dopomogai/ui';
import { adminUserService, isApiError } from '@dopomogai/api-client';
// Ensure the correct payload type is imported if you have it defined
// For now, let's define it here for clarity if it's not in `@dopomogai/types`
// but it should ideally be there.
interface AdminAddCreditsPayload {
  amount: number;
  description: string;
}
import { AgentKitError } from '../../types';

/**
 * A hook to provide functions for performing administrative actions on users.
 * It handles loading states, error reporting, and revalidating the user list on success.
 *
 * @param onActionComplete - An optional callback to run after any action successfully completes.
 */
export const useUserActions = (onActionComplete?: () => void) => {
  const { mutate } = useSWRConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AgentKitError | null>(null);

  /**
   * Generic handler for all user actions.
   * @param action - The async function from the adminUserService to execute.
   * @param successMessage - The toast message to show on success.
   * @param revalidationKey - The SWR key to revalidate on success. Defaults to the main user list.
   */
  const handleAction = useCallback(async <T>(
    action: Promise<T | AgentKitError>, 
    successMessage: string,
    revalidationKey: string | string[] = '/admin/users' // Default to revalidating the main user list
  ) => {
    setIsLoading(true);
    setError(null);
    const result = await action;
    setIsLoading(false);

    if (isApiError(result)) {
      const err = new AgentKitError(result.message, result);
      setError(err);
      toast.error(err.message || 'An unknown error occurred.');
      return false; // Indicate failure
    }
    
    toast.success(successMessage);
    // Revalidate the data cache to show the updated list.
    // We use a general key because filters might be active, and SWR will find all matching keys.
    await mutate((key) => Array.isArray(key) && key[0] === revalidationKey, undefined, { revalidate: true });
    
    if (onActionComplete) onActionComplete();
    return true; // Indicate success
  }, [mutate, onActionComplete]);

  const addCredits = useCallback(
    (userId: string, payload: AdminAddCreditsPayload) => {
      return handleAction(
        adminUserService.addCreditsToUser(userId, payload),
        `$${payload.amount.toFixed(2)} credits added successfully!`
      );
    },
    [handleAction]
  );
  
  const blockUser = useCallback(
    (userId: string) => {
      return handleAction(
        adminUserService.blockUser(userId),
        'User has been blocked.'
      );
    },
    [handleAction]
  );
  
  const unblockUser = useCallback(
    (userId: string) => {
      return handleAction(
        adminUserService.unblockUser(userId),
        'User has been unblocked.'
      );
    },
    [handleAction]
  );

  const deleteUser = useCallback(
    (userId: string) => {
       // The confirmation dialog logic is better placed in the UI component (`UserListItem.tsx`)
       // that calls this function. The hook should only contain the action logic.
       // This makes the hook more reusable.
      return handleAction(
        adminUserService.deleteUser(userId),
        'User has been deleted.'
      );
    },
    [handleAction]
  );

  return { isLoading, error, addCredits, blockUser, unblockUser, deleteUser };
};